import React, { useEffect, useState } from "react";
import { useAuth } from "../../../../../providers/AuthContext";
import { useNavigate } from "react-router-dom";
import { getWidgetById } from "../../../../../services/apiWidgests";
import Spinner from "../../../../../components/Spinner";
import DatabaseOffIco from "../../../../../components/icons/DatabaseOffIco";
import useWidgetRuntime from "../../../../../hooks/useWidgetRuntime";
import { useDashboards } from "../../../../../providers/DashboardContext";
import { useAppContext } from '../../../../../providers/AppContext';
import Modal from '../../../../../components/Modal';

const StatusRegisterWidget = ({ id }) => {
  const { addToast } = useAppContext();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [config, setConfig] = useState(null);
  const {dashboard, selectedDatarange} = useDashboards()
  const { data: runtimeData, loading: fetchingWidgetData } = useWidgetRuntime(id, dashboard?._id, undefined, JSON.stringify(selectedDatarange));
  const [fetchingWidgetInfo, setFetchingWidgetInfo] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [selectedItem, setSelectedItem] = useState(null);

  // 📥 Trae configuración del widget
  const refreshWidgetInfo = async () => {
    try {
      setFetchingWidgetInfo(true);
      const response = await getWidgetById(id, auth?.token);
      setConfig(response?.configuration || {});
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetInfo !== 'undefined') setFetchingWidgetInfo(false);
        return;
      }
      console.error("Error cargando widget:", error);
      navigate("/logout");
    } finally {
      setFetchingWidgetInfo(false);
    }
  };

  // 🔹 Inicialización
  useEffect(() => {
    refreshWidgetInfo();
  }, [selectedDatarange]);

  // Los datos llegan por socket desde el singleton compartido del backend,
  // ya ordenados alfabéticamente por el propio pipeline (ver
  // processStatusRegister.js). Se usan tal cual llegan en cada tick: si
  // antes se "congelaba" el orden de la primera carga, una tarjeta nueva
  // (proceso/máquina que recién empieza a reportar) nunca se agregaba
  // hasta refrescar la página a mano - este era el bug reportado.
  useEffect(() => {
    setData(runtimeData?.records || []);
  }, [runtimeData]);


  // 🕒 Contador de tiempo
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // 🧩 Color según reglas
  const getColorByRules = (statusValue) => {
    const rules = config?.statusField?.rules || [];
    if (!rules.length) return "#6b7280";

    for (const rule of rules) {
      const { code, value, color } = rule;
      switch (code) {
        case "eq": if (statusValue === value) return color; break;
        case "ne": if (statusValue !== value) return color; break;
        case "gt": if (!isNaN(statusValue) && Number(statusValue) > Number(value)) return color; break;
        case "lt": if (!isNaN(statusValue) && Number(statusValue) < Number(value)) return color; break;
      }
    }
    return "#6b7280";
  };

  // ⏱️ Tiempo transcurrido
  const getElapsedTime = (lastTime) => {
    const diffMs = now - new Date(lastTime).getTime();
    if (diffMs < 0) return "0s";
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  // 🔹 Render
  if (fetchingWidgetInfo || fetchingWidgetData) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Spinner />
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <span className="text-gray-700">No hay datos por el momento</span>
        <DatabaseOffIco width="40px" height="40px" fill="gray" />
      </div>
    );
  }

  return (
    <div className="w-full h-full p-4 overflow-y-auto">
      <div className="w-full text-center border-b border-gray-300 pb-2 mb-4">
        <span className="font-semibold text-gray-700 text-lg">
          Estado por {config?.groupList?.alias || "grupo"}
        </span>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-3">
        {data.map((item, idx) => {
          const color = getColorByRules(item.lastStatus);
          const elapsed = getElapsedTime(item.lastTime);
          const formattedTime = new Date(item.lastTime).toLocaleString("es-ES", {
            hour12: false,
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          });

          return (
            <div
              key={idx}
              onClick={() => setSelectedItem({ ...item, color, elapsed, formattedTime })}
              className="flex items-center p-3 bg-white shadow-sm rounded-xl border border-gray-200 hover:shadow-md hover:border-gray-300 transition-shadow cursor-pointer"
            >
              <div className="flex items-center gap-3 basis-1/12">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0 animate-pulse"
                  style={{ backgroundColor: color, boxShadow: `0 0 4px 1px ${color}` }}
                />
              </div>
              <div className="flex flex-col items-center basis-11/12 min-w-0">
                <div className="flex w-full justify-between gap-2">
                  <span className="font-bold text-gray-800 truncate">{item.valorAgrupado}</span>
                  <span className="font-semibold text-gray-700 truncate flex-shrink-0">
                    {item.lastStatus} <span className="text-sm text-gray-400">({elapsed})</span>
                  </span>
                </div>
                <div className="flex w-full">
                  <span className="text-sm text-gray-500 truncate w-full">{item.lastDetail}</span>
                </div>
                <div className="flex relative w-full justify-end">
                  <span className=" absolute text-xs  text-gray-500">{formattedTime}</span>
                </div>
              </div>

            </div>
          );
        })}
      </div>

      <Modal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        title={selectedItem ? `${config?.groupList?.alias || "Grupo"}: ${selectedItem.valorAgrupado}` : ""}
        customType={2}
        colorTitle="bg-bluesens"
        size="w-full max-w-lg"
      >
        {selectedItem && (
          <div className="flex flex-col gap-4 pt-2">
            <div className="flex items-center gap-3">
              <div
                className="w-4 h-4 rounded-full flex-shrink-0 animate-pulse"
                style={{ backgroundColor: selectedItem.color, boxShadow: `0 0 4px 1px ${selectedItem.color}` }}
              />
              <span
                className="px-3 py-1 rounded-full text-sm font-semibold text-white"
                style={{ backgroundColor: selectedItem.color }}
              >
                {selectedItem.lastStatus}
              </span>
              <span className="text-sm text-gray-400">hace {selectedItem.elapsed}</span>
            </div>

            <div>
              <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Detalle</span>
              <p className="text-gray-700 whitespace-pre-wrap break-words bg-gray-50 border border-gray-200 rounded-lg p-3">
                {selectedItem.lastDetail || "Sin detalle"}
              </p>
            </div>

            <div className="flex justify-between border-t border-gray-200 pt-3">
              <div>
                <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  {config?.groupList?.alias || "Grupo"}
                </span>
                <span className="text-gray-700 font-medium">{selectedItem.valorAgrupado}</span>
              </div>
              <div className="text-right">
                <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Última actualización</span>
                <span className="text-gray-700 font-medium">{selectedItem.formattedTime}</span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StatusRegisterWidget;
